import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'

import {
  convertTimestampToDate,
  formatAmount,
  formatCustomTrim,
  getDayStartAndEndTimestamps,
  getTimeStamp,
  getUserRole,
  hasAccess
} from '../../helper/function'
import { FilterIcon } from '../../helper/imagepath'
import { SpinnerContext } from '../../context/spinnerContext'
import { deleteCoupons, getCoupons } from '../../redux/slices/couponsSlice'
import { errorToast, successToast } from '../../util/Toast/toast'

import CustomModal from '../../component/customModal'
import Pagination from '../../component/common/pagination'
import DeletePopup from '../../component/deletePopup'
import AddEditCouponsModal from './couponsControl/AddEditCouponsModal'

import CouponsFilter from './couponsFilter'

const Coupons = () => {
  const dispatch = useDispatch()
  const { changeLoader } = useContext(SpinnerContext)
  const { allCouponsInfo } = useSelector((state) => state.coupons)
  const [showFilter, setShowFilter] = useState(false)
  const [filterData, setFilterData] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(6)
  const [showModal, setShowModal] = useState(false)
  const [isRefresh, setIsRefresh] = useState(true)
  const [couponsId, setCouponsId] = useState(null)
  const [editData, setEditData] = useState(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const openDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen)

  const toggleModal = () => setShowModal(!showModal)

  useEffect(() => {
    if (!isRefresh) {
      return
    }
    getCouponsInfo()
  }, [filterData, itemsPerPage, currentPage, isRefresh])

  const getCouponsInfo = () => {
    const { endOfDayTimestamp } = getDayStartAndEndTimestamps(getTimeStamp(filterData?.created_end))

    const params = {
      page: currentPage,
      limit: itemsPerPage || 6,
      sort_column: 'created_at',
      sort_order: 'DESC',
      amount_limit: filterData?.amount,
      ...(filterData?.coupon_code && { code: formatCustomTrim(filterData.coupon_code) }),
      ...(filterData?.name && { name: formatCustomTrim(filterData.name) }),
      application_from_date: filterData?.created_start && getTimeStamp(filterData?.created_start),
      application_till_date: filterData?.created_end && endOfDayTimestamp,
      discount: filterData?.discount || 0
    }
    changeLoader(true)
    dispatch(getCoupons(params))
      .then(({ payload }) => {
        if (payload?.status === 200) {
          changeLoader(false)
          setIsRefresh(false)
        } else {
          errorToast(payload?.data?.status_message || 'Somthing went wrong!')
          changeLoader(false)
        }
      })
      .catch(() => {
        setIsRefresh(false)
        changeLoader(false)
      })
  }

  const handlePagination = async (page, pageSize) => {
    setCurrentPage(page)
    setItemsPerPage(pageSize)
    setIsRefresh(true)
  }

  const handlePageChange = (selectedItem) => {
    setCurrentPage(selectedItem.selected + 1) // Add 1 to the index to start from 1
    setIsRefresh(true)
  }

  const checkCouponsColor = useCallback((type) => {
    return type === 'flat' ? 'coupons_black' : 'coupons_red'
  }, [])

  const onDelete = () => {
    if (couponsId) {
      changeLoader(true)
      dispatch(deleteCoupons(couponsId)).then(({ payload, error }) => {
        if (payload?.status === 200) {
          changeLoader(false)
          openDeleteModal()
          setIsRefresh(true)
          allCouponsInfo?.currentRecords == 1 && setCurrentPage((prev) => prev - 1)
          setCouponsId(null)
          successToast(payload?.data?.status_message || 'Success!')
        } else {
          openDeleteModal()
          errorToast(payload?.data?.status_message || error?.message)
          changeLoader(false)
        }
      })
    }
  }

  const onHandleAction = (coupon, action) => {
    setCouponsId(coupon?.coupon_id)
    if (action === 'delete') {
      openDeleteModal()
    } else if (action === 'edit') {
      toggleModal()
      setEditData(coupon)
    }
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid coupons-wrap">
        <div className="page-header">
          <div className="content-page-header">
            <h5>Coupons</h5>
            <div className="list-btn">
              <ul className="filter-list">
                <li>
                  <Link className="btn btn-filters w-auto popup-toggle" onClick={() => setShowFilter(!showFilter)}>
                    <span className="me-2">
                      <img src={FilterIcon} alt="filter" />
                    </span>
                    Filter
                  </Link>
                </li>

                {hasAccess('addEditDeleteCoupon', getUserRole()) && (
                  <li>
                    <Button
                      className="btn btn-primary"
                      onClick={() => {
                        toggleModal()
                        setCouponsId(null)
                        setEditData(null)
                      }}
                    >
                      New Coupon
                    </Button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="row col_space_div">
          {allCouponsInfo?.totalRecords > 0 ? (
            allCouponsInfo?.Coupons?.map((item, index) => (
              <div className="col-md-12 col-lg-6 col-xl-6" key={`coupons-${index}`}>
                <div className="coupons_card_div d-flex">
                  <div className={`coupons-offer-tag ${checkCouponsColor(item?.coupon_type)}`}>
                    {item?.coupon_type === 'flat' ? `${formatAmount(item?.discount)} OFF` : `${item?.discount}% OFF`}
                    <div className="coupons_dots_main_div">
                      <div className="coupons_dots_box"></div>
                      <div className="coupons_dots_box"></div>
                      <div className="coupons_dots_box"></div>
                    </div>
                  </div>
                  <div className="coupons-details">
                    <div className="coupons-header d-flex mb-2 justify-space-between">
                      <h6 title={item?.name}>{item?.name}</h6>
                      {hasAccess('addEditDeleteCoupon', getUserRole()) && (
                        <div className="d-flex action_div justify-content-end ">
                          <div className="edit_btn_div me-2" onClick={() => onHandleAction(item, 'edit')}>
                            <i className="fas fa-edit"></i>
                          </div>
                          <div className="delete_btn_div" onClick={() => onHandleAction(item, 'delete')}>
                            <i className="fas fa-trash-alt"></i>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="coupons-content">
                      <p>
                        Expires on:{' '}
                        <span>{item?.application_till_date ? convertTimestampToDate(item?.application_till_date, 'MMM.DD,YYYY') : '--'}</span>
                      </p>
                      <div className="d-flex coupons-amount justify-space-between">
                        <p className="mb-0">
                          Min Amount: <span>{formatAmount(item?.amount_limit || 0)}</span>
                        </p>
                        <p className="mb-0">
                          Used: <span>{item?.max_uses || 0}</span>
                        </p>
                      </div>
                    </div>
                    <div className="coupons-border"></div>
                    <div className="coupons-code d-flex">
                      <p className="mb-0">Code:</p>
                      <div className="coupons_code_box">{item?.code}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="common_card_div upcoming_cleanings_space_div">
              <p style={{ textAlign: 'center' }}>{`No Coupons Found`}</p>
            </div>
          )}
        </div>

        {allCouponsInfo?.Coupons?.length > 0 && (
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalPages={allCouponsInfo?.totalPage || 0}
            itemsPerPage={itemsPerPage}
          />
        )}

        <CouponsFilter showFilter={showFilter} setShowFilter={setShowFilter} setFilterData={setFilterData} handlePagination={handlePagination} />

        <CustomModal isOpen={showModal} toggle={toggleModal} title={couponsId ? `Edit Coupon` : `New Coupon`}>
          <AddEditCouponsModal couponID={couponsId} toggle={toggleModal} setIsRefresh={setIsRefresh} editData={editData} />
        </CustomModal>

        {isDeleteModalOpen && (
          <DeletePopup isDeleteModalOpen={isDeleteModalOpen} toggle={openDeleteModal}>
            <div className="multi_btn_div">
              <button type="button" className="btn common-outline-btn w-100" onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger common-field-btn w-100"
                onClick={(e) => {
                  e.preventDefault()
                  onDelete()
                }}
              >
                Delete
              </button>
            </div>
          </DeletePopup>
        )}
      </div>
    </div>
  )
}

export default Coupons
